/**
 * Document Invoice Service
 * Handles document invoices (GST invoices) that are part of the sales workflow
 * This is separate from billing/payment invoices
 */

import { apiClient } from './api'
import { Invoice, InvoiceStatus } from '../types/payment'

export interface DocumentInvoiceFilters {
  page?: number
  per_page?: number
  skip?: number
  limit?: number
  search?: string
  status?: InvoiceStatus
  customer_id?: string
  from_date?: string
  to_date?: string
}

export interface DocumentInvoiceListResponse {
  invoices: Invoice[]
  total: number
  skip: number
  limit: number
}

class DocumentInvoiceService {
  private baseUrl = '/admin/invoices'

  /**
   * Get list of document invoices with filters
   */
  async getInvoices(filters: DocumentInvoiceFilters = {}): Promise<DocumentInvoiceListResponse> {
    const params = new URLSearchParams()
    
    // Convert page/per_page to skip/limit for backend compatibility
    if (filters.page !== undefined && filters.per_page !== undefined) {
      const skip = (filters.page - 1) * filters.per_page
      params.append('skip', skip.toString())
      params.append('limit', filters.per_page.toString())
    } else {
      if (filters.skip !== undefined) params.append('skip', filters.skip.toString())
      if (filters.limit !== undefined) params.append('limit', filters.limit.toString())
    }
    
    // Add other filters
    if (filters.search) params.append('search', filters.search)
    if (filters.status) params.append('invoice_status', filters.status)
    if (filters.customer_id) params.append('customer_id', filters.customer_id)
    if (filters.from_date) params.append('from_date', filters.from_date)
    if (filters.to_date) params.append('to_date', filters.to_date)

    const queryString = params.toString()
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl
    const response = await apiClient.get(url)
    return response.data
  }

  /**
   * Get a single document invoice by ID
   */
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const response = await apiClient.get(`${this.baseUrl}/${invoiceId}`)
    return response.data
  }

  /**
   * Create a new invoice
   */
  async createInvoice(invoiceData: any): Promise<Invoice> {
    const response = await apiClient.post(this.baseUrl, invoiceData)
    return response.data
  }

  /**
   * Update an existing invoice
   */
  async updateInvoice(invoiceId: string, invoiceData: any): Promise<Invoice> {
    const response = await apiClient.put(`${this.baseUrl}/${invoiceId}`, invoiceData)
    return response.data
  }

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(invoiceId: string, status: InvoiceStatus, notes?: string): Promise<{
    success: boolean
    message: string
    data: any
  }> {
    const response = await apiClient.patch(`${this.baseUrl}/${invoiceId}/status`, { 
      status,
      notes 
    })
    return response.data
  }

  /**
   * Send invoice via email
   */
  async sendInvoice(invoiceId: string, email?: string): Promise<{
    success: boolean
    message: string
  }> {
    const response = await apiClient.post(`${this.baseUrl}/${invoiceId}/send`, { email })
    return response.data
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoicePDF(invoiceId: string, template: string = 'tally_prime'): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${invoiceId}/pdf`, {
      params: { template },
      responseType: 'blob'
    })
    return response.data
  }
}

export const documentInvoiceService = new DocumentInvoiceService()
